/**
 * MASTER — RECIPE / «Рецепт», Stage 1: HEADER + INGREDIENTS.
 *
 * This is the first structured slice of the professional Recipe Master.
 * It intentionally covers only:
 *   – recipe header (eyebrow, title, subtitle/lead, yield, mould/size);
 *   – one optional main image (uses existing shared image controls);
 *   – structured ingredient groups with reorderable rows.
 *
 * Stage 2 (technology, temperatures, critical points, storage) will be
 * added later — not in this sprint. Do not extend without a new brief.
 *
 * Storage:
 *   All new data lives inside `block.content.recipe`. Legacy top-level
 *   `content.eyebrow / headline / lead / body / ...` are preserved
 *   untouched so nothing is lost from prior authoring sessions.
 *
 * Reused primitives:
 *   – ImageSlot, MediaLibraryPicker (from HeroMaster).
 *   – CropModal (shared).
 */
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  ArrowUp, ArrowDown, Trash2, Plus, ChefHat,
} from "lucide-react";
import CropModal from "@/components/editorial/CropModal";
import {
  ImageSlot,
  MediaLibraryPicker,
} from "@/components/editorial/HeroMaster";

// -------------------- content shape helpers --------------------

const genId = (p = "id") =>
  `${p}${Date.now().toString(36)}${Math.floor(Math.random() * 1e4)}`;

// Read the structured recipe payload from a block, falling back to legacy
// top-level content fields the first time the block is opened after the
// switch-over. Never mutates input: returns a normalised local copy.
function readRecipe(rawContent) {
  const c = rawContent || {};
  const r = (c.recipe && typeof c.recipe === "object") ? c.recipe : {};
  const emptyRow = () => ({ id: genId("r"), name: "", qty: "", unit: "", note: "" });
  const emptyGroup = () => ({ id: genId("g"), title: "", rows: [emptyRow()] });

  const groups = Array.isArray(r.ingredient_groups) && r.ingredient_groups.length
    ? r.ingredient_groups.map((g) => ({
        id: g.id || genId("g"),
        title: g.title || "",
        rows: (Array.isArray(g.rows) ? g.rows : []).map((row) => ({
          id: row.id || genId("r"),
          name: row.name || "",
          qty: row.qty || "",
          unit: row.unit || "",
          note: row.note || "",
        })),
      }))
    : [emptyGroup()];

  const steps = Array.isArray(r.technology_steps)
    ? r.technology_steps.map((s) => ({
        id:          s.id || genId("s"),
        title:       s.title || "",
        instruction: s.instruction || "",
        time:        s.time || "",
        temperature: s.temperature || "",
        image: {
          media_id: s.image?.media_id ?? null,
          url:      s.image?.url      ?? null,
          focal_x:  s.image?.focal_x  ?? 0.5,
          focal_y:  s.image?.focal_y  ?? 0.5,
          zoom:     s.image?.zoom     ?? 1,
          alt:      s.image?.alt      ?? "",
        },
      }))
    : [];

  const readNoteList = (arr, prefix) => (Array.isArray(arr) ? arr : []).map((n) => ({
    id:    n.id || genId(prefix),
    title: n.title || "",
    text:  n.text || "",
  }));
  const critical_points   = readNoteList(r.critical_points,   "cp");
  const professional_tips = readNoteList(r.professional_tips, "pt");

  const storage_items = (Array.isArray(r.storage_items) ? r.storage_items : []).map((s) => ({
    id:        s.id || genId("st"),
    condition: s.condition || "",
    duration:  s.duration  || "",
    note:      s.note      || "",
  }));

  return {
    // header
    eyebrow:  r.eyebrow  ?? c.eyebrow  ?? "",
    title:    r.title    ?? c.headline ?? "",
    subtitle: r.subtitle ?? c.lead     ?? "",
    yield:    r.yield    ?? "",
    mould:    r.mould    ?? "",
    // hero image (single, optional)
    hero: {
      media_id: r.hero?.media_id ?? null,
      url:      r.hero?.url      ?? null,
      focal_x:  r.hero?.focal_x  ?? 0.5,
      focal_y:  r.hero?.focal_y  ?? 0.5,
      zoom:     r.hero?.zoom     ?? 1,
      alt:      r.hero?.alt      ?? "",
    },
    ingredient_groups: groups,
    technology_steps: steps,
    critical_points,
    professional_tips,
    storage_items,
  };
}

// -------------------- Editor --------------------

// Shared editor panel for a simple ordered list of notes (id + title + text).
// Used by both «Критические точки» and «Профессиональные советы».
function NoteListPanel({
  title, addLabel, testidBase, titlePlaceholder, textPlaceholder, items, controls,
}) {
  return (
    <div className="xl:col-span-2 rounded-2xl bg-white border hairline p-4 space-y-3"
         data-testid={`${testidBase}-editor`}>
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-widest opacity-70">{title}</div>
        <button type="button" onClick={controls.add}
          className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
          data-testid={`${testidBase}-add`}>
          <Plus className="w-3.5 h-3.5" /> {addLabel}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg bg-cream/60 border border-dashed border-espresso/25 p-4 text-center">
          <div className="text-[12px] opacity-70 mb-2">
            Пока ничего не добавлено. Пункты этой секции не будут показаны в предпросмотре, пока они пусты.
          </div>
          <button type="button" onClick={controls.add}
            className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-espresso text-cream"
            data-testid={`${testidBase}-empty-add`}>
            <Plus className="w-3.5 h-3.5" /> {addLabel}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((n, i) => (
            <div key={n.id} className="rounded-lg border hairline bg-cream/40 p-3 space-y-2"
                 data-testid={`${testidBase}-item-${i}`}>
              <div className="flex items-center gap-2">
                <Input value={n.title}
                       onChange={(e) => controls.setField(i, { title: e.target.value })}
                       placeholder={titlePlaceholder}
                       className="font-serif"
                       data-testid={`${testidBase}-title-${i}`} />
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => controls.move(i, -1)} disabled={i === 0}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Выше" data-testid={`${testidBase}-up-${i}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => controls.move(i, +1)} disabled={i === items.length - 1}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Ниже" data-testid={`${testidBase}-down-${i}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => controls.remove(i)}
                    className="p-1 opacity-60 hover:opacity-100 text-red-600"
                    title="Удалить" data-testid={`${testidBase}-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <textarea rows={3} value={n.text}
                        onChange={(e) => controls.setField(i, { text: e.target.value })}
                        className="w-full border rounded px-2 py-1.5 text-sm font-serif leading-relaxed"
                        placeholder={textPlaceholder}
                        data-testid={`${testidBase}-text-${i}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Editor panel for «Хранение и срок годности».
// Each row is a condition (long text), a compact duration, and an optional
// free-form note. Stable IDs and order are preserved by the parent state.
function StorageListPanel({ items, controls }) {
  return (
    <div className="xl:col-span-2 rounded-2xl bg-white border hairline p-4 space-y-3"
         data-testid="recipe-storage-editor">
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Хранение и срок годности</div>
        <button type="button" onClick={controls.add}
          className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
          data-testid="recipe-storage-add">
          <Plus className="w-3.5 h-3.5" /> Добавить условие хранения
        </button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg bg-cream/60 border border-dashed border-espresso/25 p-4 text-center">
          <div className="text-[12px] opacity-70 mb-2">
            Ни одного условия хранения не добавлено. Секция не будет показана в предпросмотре, пока она пуста.
          </div>
          <button type="button" onClick={controls.add}
            className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-espresso text-cream"
            data-testid="recipe-storage-empty-add">
            <Plus className="w-3.5 h-3.5" /> Добавить условие хранения
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((s, i) => (
            <div key={s.id} className="rounded-lg border hairline bg-cream/40 p-3 space-y-2"
                 data-testid={`recipe-storage-item-${i}`}>
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,3fr)_minmax(0,1fr)_auto] gap-2 items-center">
                <Input value={s.condition}
                       onChange={(e) => controls.setField(i, { condition: e.target.value })}
                       placeholder="Условие · например «При комнатной температуре»"
                       className="font-serif"
                       data-testid={`recipe-storage-condition-${i}`} />
                <Input value={s.duration}
                       onChange={(e) => controls.setField(i, { duration: e.target.value })}
                       placeholder="Срок · например «до 2 суток»"
                       className="font-serif"
                       data-testid={`recipe-storage-duration-${i}`} />
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => controls.move(i, -1)} disabled={i === 0}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Выше" data-testid={`recipe-storage-up-${i}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => controls.move(i, +1)} disabled={i === items.length - 1}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Ниже" data-testid={`recipe-storage-down-${i}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => controls.remove(i)}
                    className="p-1 opacity-60 hover:opacity-100 text-red-600"
                    title="Удалить" data-testid={`recipe-storage-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <Input value={s.note}
                     onChange={(e) => controls.setField(i, { note: e.target.value })}
                     placeholder="Примечание · необязательно"
                     data-testid={`recipe-storage-note-${i}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



export function RecipeEditor({ block, onPatch }) {
  const r = useMemo(() => readRecipe(block.content), [block.content]);
  // Patch only the structured recipe payload — legacy top-level content
  // fields are preserved verbatim.
  const patch = (delta) => onPatch({
    content: { ...(block.content || {}), recipe: { ...r, ...delta } },
  });

  const [libraryOpen, setLibraryOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  // Per-step media pickers — null | step index. Kept separate from the hero
  // pickers so opening one never closes/mutates the other.
  const [stepLibraryFor, setStepLibraryFor] = useState(null);
  const [stepCropFor, setStepCropFor] = useState(null);

  // -- hero image --
  const setHero = (next) => patch({ hero: { ...r.hero, ...next } });
  const acceptFromLibrary = (m) => {
    setHero({ media_id: m.id, url: m.file_url || m.url });
    setLibraryOpen(false);
  };
  const saveCrop = ({ focal_x, focal_y, zoom }) => {
    setHero({ focal_x, focal_y, zoom });
    setCropOpen(false);
  };

  // -- ingredient groups --
  const setGroups = (groups) => patch({ ingredient_groups: groups });
  const addGroup = () => setGroups([
    ...r.ingredient_groups,
    { id: genId("g"), title: "", rows: [{ id: genId("r"), name: "", qty: "", unit: "", note: "" }] },
  ]);
  const removeGroup = (gi) => {
    const next = [...r.ingredient_groups];
    next.splice(gi, 1);
    setGroups(next.length ? next : [{ id: genId("g"), title: "", rows: [{ id: genId("r"), name: "", qty: "", unit: "", note: "" }] }]);
  };
  const moveGroup = (gi, dir) => {
    const next = [...r.ingredient_groups];
    const j = gi + dir;
    if (j < 0 || j >= next.length) return;
    [next[gi], next[j]] = [next[j], next[gi]];
    setGroups(next);
  };
  const patchGroup = (gi, delta) => {
    const next = [...r.ingredient_groups];
    next[gi] = { ...next[gi], ...delta };
    setGroups(next);
  };
  const addRow = (gi) => {
    const g = r.ingredient_groups[gi];
    patchGroup(gi, {
      rows: [...g.rows, { id: genId("r"), name: "", qty: "", unit: "", note: "" }],
    });
  };
  const removeRow = (gi, ri) => {
    const g = r.ingredient_groups[gi];
    const rows = [...g.rows]; rows.splice(ri, 1);
    patchGroup(gi, { rows: rows.length ? rows : [{ id: genId("r"), name: "", qty: "", unit: "", note: "" }] });
  };
  const moveRow = (gi, ri, dir) => {
    const g = r.ingredient_groups[gi];
    const rows = [...g.rows];
    const j = ri + dir;
    if (j < 0 || j >= rows.length) return;
    [rows[ri], rows[j]] = [rows[j], rows[ri]];
    patchGroup(gi, { rows });
  };
  const patchRow = (gi, ri, delta) => {
    const g = r.ingredient_groups[gi];
    const rows = [...g.rows];
    rows[ri] = { ...rows[ri], ...delta };
    patchGroup(gi, { rows });
  };

  // -- technology steps --
  const setSteps = (steps) => patch({ technology_steps: steps });
  const emptyStep = () => ({
    id: genId("s"), title: "", instruction: "", time: "", temperature: "",
    image: { media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1, alt: "" },
  });
  const addStep = () => setSteps([...r.technology_steps, emptyStep()]);
  const removeStep = (si) => {
    const next = [...r.technology_steps]; next.splice(si, 1); setSteps(next);
  };
  const moveStep = (si, dir) => {
    const next = [...r.technology_steps];
    const j = si + dir;
    if (j < 0 || j >= next.length) return;
    [next[si], next[j]] = [next[j], next[si]];
    setSteps(next);
  };
  const patchStep = (si, delta) => {
    const next = [...r.technology_steps];
    next[si] = { ...next[si], ...delta };
    setSteps(next);
  };
  const patchStepImage = (si, delta) => {
    const s = r.technology_steps[si];
    patchStep(si, { image: { ...s.image, ...delta } });
  };
  const acceptStepFromLibrary = (m) => {
    if (stepLibraryFor == null) return;
    patchStepImage(stepLibraryFor, { media_id: m.id, url: m.file_url || m.url });
    setStepLibraryFor(null);
  };
  const saveStepCrop = ({ focal_x, focal_y, zoom }) => {
    if (stepCropFor == null) return;
    patchStepImage(stepCropFor, { focal_x, focal_y, zoom });
    setStepCropFor(null);
  };

  // -- generic note-list controls (critical_points / professional_tips) --
  // Returns a stable set of CRUD helpers bound to `key` (either
  // "critical_points" or "professional_tips"). All operations preserve
  // stable IDs and current order.
  const makeNoteControls = (key, idPrefix) => ({
    add: () => patch({ [key]: [...r[key], { id: genId(idPrefix), title: "", text: "" }] }),
    remove: (i) => {
      const next = [...r[key]]; next.splice(i, 1); patch({ [key]: next });
    },
    move: (i, dir) => {
      const next = [...r[key]];
      const j = i + dir;
      if (j < 0 || j >= next.length) return;
      [next[i], next[j]] = [next[j], next[i]];
      patch({ [key]: next });
    },
    setField: (i, delta) => {
      const next = [...r[key]];
      next[i] = { ...next[i], ...delta };
      patch({ [key]: next });
    },
  });
  const cp = makeNoteControls("critical_points", "cp");
  const pt = makeNoteControls("professional_tips", "pt");

  // -- storage items (condition / duration / optional note) --
  const st = {
    add: () => patch({
      storage_items: [
        ...r.storage_items,
        { id: genId("st"), condition: "", duration: "", note: "" },
      ],
    }),
    remove: (i) => {
      const next = [...r.storage_items]; next.splice(i, 1);
      patch({ storage_items: next });
    },
    move: (i, dir) => {
      const next = [...r.storage_items];
      const j = i + dir;
      if (j < 0 || j >= next.length) return;
      [next[i], next[j]] = [next[j], next[i]];
      patch({ storage_items: next });
    },
    setField: (i, delta) => {
      const next = [...r.storage_items];
      next[i] = { ...next[i], ...delta };
      patch({ storage_items: next });
    },
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4"
         data-testid="recipe-editor">
      {/* LEFT — header + hero image */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Шапка рецепта</div>

        <div>
          <label className="block text-[11px] opacity-60 mb-1">Надзаголовок · необязательно</label>
          <Input value={r.eyebrow} onChange={(e) => patch({ eyebrow: e.target.value })}
                 placeholder="БИСКВИТЫ · 04" data-testid="recipe-field-eyebrow" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Название рецепта</label>
          <Input value={r.title} onChange={(e) => patch({ title: e.target.value })}
                 className="font-serif text-lg" data-testid="recipe-field-title" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Подзаголовок · лид · необязательно</label>
          <textarea rows={2} value={r.subtitle}
                    onChange={(e) => patch({ subtitle: e.target.value })}
                    className="w-full border rounded px-2 py-1.5 text-sm italic"
                    data-testid="recipe-field-subtitle" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] opacity-60 mb-1">Выход · необязательно</label>
            <Input value={r.yield} onChange={(e) => patch({ yield: e.target.value })}
                   placeholder="12 порций" data-testid="recipe-field-yield" />
          </div>
          <div>
            <label className="block text-[11px] opacity-60 mb-1">Форма / размер · необязательно</label>
            <Input value={r.mould} onChange={(e) => patch({ mould: e.target.value })}
                   placeholder="форма Ø 18 см" data-testid="recipe-field-mould" />
          </div>
        </div>

        <div className="pt-2">
          <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">
            Главное фото · необязательно
          </div>
          <ImageSlot
            label=""
            url={r.hero.url}
            focalX={r.hero.focal_x} focalY={r.hero.focal_y} zoom={r.hero.zoom}
            onFile={(m) => setHero({ media_id: m.id, url: m.file_url })}
            onClear={() => setHero({ media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
            onOpenLibrary={() => setLibraryOpen(true)}
            onOpenCrop={() => setCropOpen(true)}
            testid="recipe-hero-slot" />
          <div className="mt-2">
            <Input placeholder="Alt-текст фото · необязательно"
                   value={r.hero.alt}
                   onChange={(e) => setHero({ alt: e.target.value })}
                   data-testid="recipe-hero-alt" />
          </div>
        </div>
      </div>

      {/* RIGHT — ingredient groups */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-3">
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] uppercase tracking-widest opacity-70">Ингредиенты</div>
          <button type="button" onClick={addGroup}
            className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
            data-testid="recipe-add-group">
            <Plus className="w-3.5 h-3.5" /> Добавить группу
          </button>
        </div>

        <div className="space-y-3">
          {r.ingredient_groups.map((g, gi) => (
            <div key={g.id} className="rounded-lg border hairline bg-cream/40 p-3"
                 data-testid={`recipe-group-${gi}`}>
              <div className="flex items-center gap-2 mb-2">
                <Input value={g.title}
                       onChange={(e) => patchGroup(gi, { title: e.target.value })}
                       placeholder="Название группы · например «Бисквит»"
                       className="font-serif"
                       data-testid={`recipe-group-title-${gi}`} />
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => moveGroup(gi, -1)} disabled={gi === 0}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Выше" data-testid={`recipe-group-up-${gi}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => moveGroup(gi, +1)} disabled={gi === r.ingredient_groups.length - 1}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Ниже" data-testid={`recipe-group-down-${gi}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => removeGroup(gi)}
                    className="p-1 opacity-60 hover:opacity-100 text-red-600"
                    title="Удалить группу" data-testid={`recipe-group-remove-${gi}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {g.rows.map((row, ri) => (
                  <div key={row.id}
                       className="grid grid-cols-[minmax(0,3fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,2fr)_auto] gap-2 items-center"
                       data-testid={`recipe-row-${gi}-${ri}`}>
                    <Input value={row.name}
                           onChange={(e) => patchRow(gi, ri, { name: e.target.value })}
                           placeholder="Ингредиент"
                           data-testid={`recipe-row-name-${gi}-${ri}`} />
                    <Input value={row.qty}
                           onChange={(e) => patchRow(gi, ri, { qty: e.target.value })}
                           placeholder="Кол-во"
                           data-testid={`recipe-row-qty-${gi}-${ri}`} />
                    <Input value={row.unit}
                           onChange={(e) => patchRow(gi, ri, { unit: e.target.value })}
                           placeholder="Ед."
                           data-testid={`recipe-row-unit-${gi}-${ri}`} />
                    <Input value={row.note}
                           onChange={(e) => patchRow(gi, ri, { note: e.target.value })}
                           placeholder="Примечание · необязательно"
                           data-testid={`recipe-row-note-${gi}-${ri}`} />
                    <div className="flex items-center gap-0.5">
                      <button type="button" onClick={() => moveRow(gi, ri, -1)} disabled={ri === 0}
                        className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                        title="Выше" data-testid={`recipe-row-up-${gi}-${ri}`}>
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => moveRow(gi, ri, +1)} disabled={ri === g.rows.length - 1}
                        className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                        title="Ниже" data-testid={`recipe-row-down-${gi}-${ri}`}>
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => removeRow(gi, ri)}
                        className="p-1 opacity-60 hover:opacity-100 text-red-600"
                        title="Удалить строку" data-testid={`recipe-row-remove-${gi}-${ri}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button type="button" onClick={() => addRow(gi)}
                className="mt-2 text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
                data-testid={`recipe-add-row-${gi}`}>
                <Plus className="w-3.5 h-3.5" /> Добавить ингредиент
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* FULL-WIDTH — technology steps */}
      <div className="xl:col-span-2 rounded-2xl bg-white border hairline p-4 space-y-3"
           data-testid="recipe-technology-editor">
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] uppercase tracking-widest opacity-70">Технология</div>
          <button type="button" onClick={addStep}
            className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
            data-testid="recipe-add-step">
            <Plus className="w-3.5 h-3.5" /> Добавить шаг
          </button>
        </div>

        {r.technology_steps.length === 0 && (
          <div className="rounded-lg bg-cream/60 border border-dashed border-espresso/25 p-4 text-center">
            <div className="text-[12px] opacity-70 mb-2">
              Пока нет ни одного шага. Добавьте первый шаг, чтобы описать технологию приготовления.
            </div>
            <button type="button" onClick={addStep}
              className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-espresso text-cream"
              data-testid="recipe-empty-add-step">
              <Plus className="w-3.5 h-3.5" /> Добавить первый шаг
            </button>
          </div>
        )}

        <div className="space-y-3">
          {r.technology_steps.map((s, si) => (
            <div key={s.id} className="rounded-lg border hairline bg-cream/40 p-3 space-y-3"
                 data-testid={`recipe-step-${si}`}>
              <div className="flex items-center gap-2">
                <div className="text-[10px] uppercase tracking-widest opacity-60 shrink-0 font-mono">
                  {String(si + 1).padStart(2, "0")}
                </div>
                <Input value={s.title}
                       onChange={(e) => patchStep(si, { title: e.target.value })}
                       placeholder="Название шага · например «Взбить желтки»"
                       className="font-serif"
                       data-testid={`recipe-step-title-${si}`} />
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => moveStep(si, -1)} disabled={si === 0}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Выше" data-testid={`recipe-step-up-${si}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => moveStep(si, +1)} disabled={si === r.technology_steps.length - 1}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    title="Ниже" data-testid={`recipe-step-down-${si}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => removeStep(si)}
                    className="p-1 opacity-60 hover:opacity-100 text-red-600"
                    title="Удалить шаг" data-testid={`recipe-step-remove-${si}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_240px] gap-3">
                <div className="space-y-2">
                  <div>
                    <label className="block text-[11px] opacity-60 mb-1">Инструкция</label>
                    <textarea rows={4} value={s.instruction}
                              onChange={(e) => patchStep(si, { instruction: e.target.value })}
                              className="w-full border rounded px-2 py-1.5 text-sm font-serif leading-relaxed"
                              data-testid={`recipe-step-instruction-${si}`} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] opacity-60 mb-1">Время · необязательно</label>
                      <Input value={s.time}
                             onChange={(e) => patchStep(si, { time: e.target.value })}
                             placeholder="15 мин"
                             data-testid={`recipe-step-time-${si}`} />
                    </div>
                    <div>
                      <label className="block text-[11px] opacity-60 mb-1">Температура · необязательно</label>
                      <Input value={s.temperature}
                             onChange={(e) => patchStep(si, { temperature: e.target.value })}
                             placeholder="170 °C"
                             data-testid={`recipe-step-temp-${si}`} />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] opacity-60 mb-1">Фото · необязательно</label>
                  <ImageSlot
                    label=""
                    url={s.image.url}
                    focalX={s.image.focal_x} focalY={s.image.focal_y} zoom={s.image.zoom}
                    onFile={(m) => patchStepImage(si, { media_id: m.id, url: m.file_url })}
                    onClear={() => patchStepImage(si, { media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1 })}
                    onOpenLibrary={() => setStepLibraryFor(si)}
                    onOpenCrop={() => setStepCropFor(si)}
                    testid={`recipe-step-image-${si}`} />
                  <div className="mt-2">
                    <Input placeholder="Alt-текст · необязательно"
                           value={s.image.alt}
                           onChange={(e) => patchStepImage(si, { alt: e.target.value })}
                           data-testid={`recipe-step-image-alt-${si}`} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FULL-WIDTH — critical points + professional tips */}
      <NoteListPanel
        title="ВАЖНО"
        testidBase="recipe-critical"
        addLabel="Добавить важное замечание"
        titlePlaceholder="Например «Температура выпекания»"
        textPlaceholder="Опишите, что критично для получения нужного результата…"
        items={r.critical_points}
        controls={cp} />
      <NoteListPanel
        title="СОВЕТЫ АВТОРА"
        testidBase="recipe-tip"
        addLabel="Добавить совет"
        titlePlaceholder="Например «Мука обязательно должна быть просеяна»"
        textPlaceholder="Кратко и по существу — совет от шефа…"
        items={r.professional_tips}
        controls={pt} />
      <StorageListPanel items={r.storage_items} controls={st} />

      <MediaLibraryPicker
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onPick={acceptFromLibrary} />
      <MediaLibraryPicker
        open={stepLibraryFor != null}
        onClose={() => setStepLibraryFor(null)}
        onPick={acceptStepFromLibrary} />
      <CropModal
        open={cropOpen}
        url={r.hero.url}
        focalX={r.hero.focal_x}
        focalY={r.hero.focal_y}
        zoom={r.hero.zoom}
        onClose={() => setCropOpen(false)}
        onSave={saveCrop} />
      <CropModal
        open={stepCropFor != null}
        url={stepCropFor != null ? r.technology_steps[stepCropFor]?.image?.url : null}
        focalX={stepCropFor != null ? r.technology_steps[stepCropFor]?.image?.focal_x ?? 0.5 : 0.5}
        focalY={stepCropFor != null ? r.technology_steps[stepCropFor]?.image?.focal_y ?? 0.5 : 0.5}
        zoom={stepCropFor != null ? r.technology_steps[stepCropFor]?.image?.zoom || 1 : 1}
        onClose={() => setStepCropFor(null)}
        onSave={saveStepCrop} />
    </div>
  );
}

// -------------------- Reader render --------------------

function HeroFrame({ hero, aspect = "aspect-[4/5]" }) {
  if (!hero?.url) {
    return (
      <div className={`w-full ${aspect} bg-espresso/5 border border-dashed border-espresso/20`} />
    );
  }
  return (
    <div className={`w-full ${aspect} overflow-hidden bg-neutral-100`}>
      <img
        src={hero.url}
        alt={hero.alt || ""}
        className="w-full h-full object-cover"
        style={{
          objectPosition: `${(hero.focal_x ?? 0.5) * 100}% ${(hero.focal_y ?? 0.5) * 100}%`,
          transform: `scale(${hero.zoom || 1})`,
          transformOrigin: `${(hero.focal_x ?? 0.5) * 100}% ${(hero.focal_y ?? 0.5) * 100}%`,
        }}
      />
    </div>
  );
}

// One ingredient group rendered in editorial style.
function GroupCard({ group }) {
  const hasRows = (group.rows || []).some((row) => row.name || row.qty || row.unit || row.note);
  return (
    <div className="space-y-2" data-testid={`recipe-render-group-${group.id}`}>
      {group.title && (
        <div className="text-[10px] tracking-[0.24em] uppercase pb-1 border-b border-espresso/20"
             style={{ color: "#5B4326" }}>
          {group.title}
        </div>
      )}
      {hasRows ? (
        <ul className="divide-y divide-espresso/10">
          {group.rows.map((row) => {
            if (!row.name && !row.qty && !row.unit && !row.note) return null;
            return (
              <li key={row.id}
                  className="py-2 grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-baseline">
                <div>
                  <div className="font-serif text-[15px] leading-[1.35]" style={{ color: "#2A1D14" }}>
                    {row.name}
                  </div>
                  {row.note && (
                    <div className="text-[12px] italic opacity-70 mt-0.5" style={{ color: "#5B4326" }}>
                      {row.note}
                    </div>
                  )}
                </div>
                <div className="text-[13px] tabular-nums whitespace-nowrap" style={{ color: "#3E2E20" }}>
                  {row.qty}{row.qty && row.unit ? "\u00A0" : ""}{row.unit}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

// Distribute groups into balanced columns preserving order.
function distributeGroups(groups, cols) {
  const buckets = Array.from({ length: cols }, () => []);
  const weights = new Array(cols).fill(0);
  groups.forEach((g) => {
    // approximate weight = title + rows count
    const w = 1 + (Array.isArray(g.rows) ? g.rows.length : 0);
    let target = 0;
    for (let i = 1; i < cols; i += 1) {
      if (weights[i] < weights[target]) target = i;
    }
    buckets[target].push(g);
    weights[target] += w;
  });
  return buckets;
}

// Compact chip for optional step parameters (time / temperature).
function StepParam({ label, value }) {
  return (
    <div className="inline-flex items-baseline gap-2 py-1 px-3 rounded-full bg-cream/70 border border-espresso/10">
      <span className="text-[9px] tracking-[0.24em] uppercase opacity-70" style={{ color: "#5B4326" }}>
        {label}
      </span>
      <span className="font-serif text-[13px] tabular-nums" style={{ color: "#2A1D14" }}>
        {value}
      </span>
    </div>
  );
}

// One step rendered in editorial style. Number badge sits alongside title;
// the optional image only renders when a url is present so no empty
// placeholder is ever reserved.
function StepCard({ step, index, mode }) {
  const isMobile = mode === "mobile";
  const hasImage = !!(step.image && step.image.url);
  const num = String(index + 1).padStart(2, "0");

  const NumberBadge = (
    <div className="font-serif tabular-nums leading-none"
         style={{
           color: "#A67B4A",
           fontSize: isMobile ? "34px" : "44px",
           letterSpacing: "-0.02em",
         }}
         data-testid={`recipe-step-number-${step.id}`}>
      {num}
    </div>
  );

  const Params = (step.time || step.temperature) ? (
    <div className="flex flex-wrap gap-2 mt-3">
      {step.time && <StepParam label="Время" value={step.time} />}
      {step.temperature && <StepParam label="Температура" value={step.temperature} />}
    </div>
  ) : null;

  const TextBlock = (
    <div>
      <div className="flex items-baseline gap-4">
        {NumberBadge}
        <div className="min-w-0">
          <div className="text-[9px] tracking-[0.28em] uppercase opacity-60" style={{ color: "#5B4326" }}>
            Шаг {num}
          </div>
          {step.title && (
            <h4 className="font-serif leading-[1.2] mt-1"
                style={{
                  fontSize: isMobile ? "clamp(20px, 5vw, 22px)" : "clamp(22px, 2vw, 26px)",
                  color: "#2A1D14",
                }}>
              {step.title}
            </h4>
          )}
        </div>
      </div>
      {step.instruction && (
        <p className="mt-3 font-serif text-[15px] leading-[1.7] whitespace-pre-wrap max-w-[62ch]"
           style={{ color: "#3E2E20" }}>
          {step.instruction}
        </p>
      )}
      {Params}
    </div>
  );

  if (isMobile) {
    // Mobile — everything stacked, image (if any) between params and instruction
    // read order: number + title → params → image → instruction. Simpler:
    // number+title, image, params, instruction.
    return (
      <div className="space-y-3" data-testid={`recipe-step-card-${step.id}`}>
        <div className="flex items-baseline gap-4">
          {NumberBadge}
          <div className="min-w-0">
            <div className="text-[9px] tracking-[0.28em] uppercase opacity-60" style={{ color: "#5B4326" }}>
              Шаг {num}
            </div>
            {step.title && (
              <h4 className="font-serif leading-[1.2] mt-1"
                  style={{ fontSize: "clamp(20px, 5vw, 22px)", color: "#2A1D14" }}>
                {step.title}
              </h4>
            )}
          </div>
        </div>
        {hasImage && (
          <div className="overflow-hidden bg-neutral-100 aspect-[4/5]">
            <img src={step.image.url} alt={step.image.alt || ""}
                 className="w-full h-full object-cover"
                 style={{
                   objectPosition: `${(step.image.focal_x ?? 0.5) * 100}% ${(step.image.focal_y ?? 0.5) * 100}%`,
                   transform: `scale(${step.image.zoom || 1})`,
                   transformOrigin: `${(step.image.focal_x ?? 0.5) * 100}% ${(step.image.focal_y ?? 0.5) * 100}%`,
                 }} />
          </div>
        )}
        {step.instruction && (
          <p className="font-serif text-[15px] leading-[1.7] whitespace-pre-wrap"
             style={{ color: "#3E2E20" }}>
            {step.instruction}
          </p>
        )}
        {(step.time || step.temperature) && (
          <div className="flex flex-wrap gap-2">
            {step.time && <StepParam label="Время" value={step.time} />}
            {step.temperature && <StepParam label="Температура" value={step.temperature} />}
          </div>
        )}
      </div>
    );
  }

  // Desktop — image (if any) on one side, text on the other. Alternate side
  // on even/odd steps for editorial rhythm; when no image, text runs full width.
  if (!hasImage) {
    return (
      <div data-testid={`recipe-step-card-${step.id}`}>
        {TextBlock}
      </div>
    );
  }
  const imageOnLeft = index % 2 === 0;
  const ImageCol = (
    <div className="overflow-hidden bg-neutral-100 aspect-[4/5]">
      <img src={step.image.url} alt={step.image.alt || ""}
           className="w-full h-full object-cover"
           style={{
             objectPosition: `${(step.image.focal_x ?? 0.5) * 100}% ${(step.image.focal_y ?? 0.5) * 100}%`,
             transform: `scale(${step.image.zoom || 1})`,
             transformOrigin: `${(step.image.focal_x ?? 0.5) * 100}% ${(step.image.focal_y ?? 0.5) * 100}%`,
           }} />
    </div>
  );
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-8 lg:gap-12 items-start"
         data-testid={`recipe-step-card-${step.id}`}>
      {imageOnLeft ? <>{ImageCol}<div>{TextBlock}</div></> : <><div>{TextBlock}</div>{ImageCol}</>}
    </div>
  );
}

// Reader column for a note list — Critical Points or Professional Tips.
// Kept minimal on purpose: calm hierarchy, no card frame, hairline divider
// between items, subtle accent bar based on `kind`.
function NoteColumn({ title, items, kind }) {
  const accent = kind === "critical" ? "#B8654A" : "#8A6A46";
  return (
    <div data-testid={`recipe-notes-${kind}`}>
      <div className="text-[10px] tracking-[0.28em] uppercase mb-6 pb-2 border-b border-espresso/25"
           style={{ color: "#5B4326" }}>
        {title}
      </div>
      <ul className="divide-y divide-espresso/10">
        {items.map((n) => (
          <li key={n.id} className="py-4 first:pt-0" data-testid={`recipe-note-${n.id}`}>
            {n.title && (
              <div className="flex items-baseline gap-3">
                <span className="inline-block w-1 h-4 shrink-0 mt-1"
                      style={{ background: accent }} aria-hidden />
                <h4 className="font-serif leading-[1.2] text-[18px] md:text-[20px]"
                    style={{ color: "#2A1D14" }}>
                  {n.title}
                </h4>
              </div>
            )}
            {n.text && (
              <p className={`font-serif text-[15px] leading-[1.7] whitespace-pre-wrap max-w-[62ch] ${n.title ? "mt-2 pl-4" : ""}`}
                 style={{ color: "#3E2E20" }}>
                {n.text}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}



// Reader section for «Хранение и срок годности». One calm full-width panel,
// hairline row separators, no card frame. Condition sits on the left,
// duration is emphasised on the right; the optional note appears under
// the condition only when present so no blank space is reserved.
function StorageSection({ items, mode }) {
  const isMobile = mode === "mobile";
  return (
    <div className={isMobile ? "mt-10" : "mt-14"} data-testid="recipe-storage-section">
      <div className="text-[10px] tracking-[0.28em] uppercase mb-6 pb-2 border-b border-espresso/25"
           style={{ color: "#5B4326" }}>
        Хранение и срок годности
      </div>
      <ul className="divide-y divide-espresso/10">
        {items.map((s) => (
          <li key={s.id}
              className={`py-4 first:pt-0 ${isMobile
                ? "space-y-1"
                : "grid grid-cols-[minmax(0,7fr)_minmax(0,3fr)] gap-8 items-baseline"}`}
              data-testid={`recipe-storage-row-${s.id}`}>
            <div className="min-w-0">
              {s.condition && (
                <div className="font-serif text-[16px] leading-[1.35]" style={{ color: "#2A1D14" }}>
                  {s.condition}
                </div>
              )}
              {s.note && (
                <div className="mt-1 font-serif italic text-[13px] leading-[1.5] max-w-[62ch]"
                     style={{ color: "#5B4326" }}>
                  {s.note}
                </div>
              )}
            </div>
            {s.duration && (
              <div className={`font-serif tabular-nums whitespace-nowrap ${isMobile ? "text-[14px]" : "text-[16px] text-right"}`}
                   style={{ color: "#3E2E20" }}>
                {s.duration}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}



export function RecipeRender({ block, mode = "desktop" }) {
  const r = useMemo(() => readRecipe(block.content), [block.content]);
  const isMobile = mode === "mobile";

  const Header = (
    <>
      {r.eyebrow && (
        <div className="text-[10px] tracking-[0.28em] uppercase mb-3"
             style={{ color: "#A67B4A" }}>
          {r.eyebrow}
        </div>
      )}
      {r.title && (
        <h2 className="font-serif leading-[1.05]"
            style={{
              fontSize: isMobile ? "clamp(26px, 6vw, 32px)" : "clamp(32px, 3.4vw, 46px)",
              color: "#2A1D14",
            }}>
          {r.title}
        </h2>
      )}
      {r.subtitle && (
        <p className="mt-3 font-serif italic text-[16px] leading-[1.55] max-w-[62ch]"
           style={{ color: "#5B4326" }}>
          {r.subtitle}
        </p>
      )}
    </>
  );

  const YieldCard = (r.yield || r.mould) ? (
    <div className="inline-flex flex-wrap items-center gap-x-6 gap-y-1 mt-4 py-3 px-4 rounded-lg bg-cream/70 border border-espresso/10"
         data-testid="recipe-yield-card">
      {r.yield && (
        <div className="text-[13px]" style={{ color: "#3E2E20" }}>
          <span className="text-[10px] tracking-[0.2em] uppercase opacity-70 mr-2">Выход</span>
          <span className="font-serif">{r.yield}</span>
        </div>
      )}
      {r.mould && (
        <div className="text-[13px]" style={{ color: "#3E2E20" }}>
          <span className="text-[10px] tracking-[0.2em] uppercase opacity-70 mr-2">Форма</span>
          <span className="font-serif">{r.mould}</span>
        </div>
      )}
    </div>
  ) : null;

  const groups = (r.ingredient_groups || []).filter((g) =>
    g.title || (g.rows || []).some((row) => row.name || row.qty || row.unit || row.note));

  // Skip empty technology steps — a step is considered non-empty if any
  // author-facing field carries content.
  const steps = (r.technology_steps || []).filter((s) =>
    s.title || s.instruction || s.time || s.temperature || (s.image && s.image.url));

  const TechnologySection = steps.length ? (
    <div className={isMobile ? "mt-10" : "mt-14"} data-testid="recipe-technology-section">
      <div className="text-[10px] tracking-[0.28em] uppercase mb-6 pb-2 border-b border-espresso/25"
           style={{ color: "#5B4326" }}>
        Технология
      </div>
      <div className={isMobile ? "space-y-10" : "space-y-14"}>
        {steps.map((s, i) => <StepCard key={s.id} step={s} index={i} mode={mode} />)}
      </div>
    </div>
  ) : null;

  // Skip empty note items — a note counts only if it has a title or text.
  const criticals = (r.critical_points   || []).filter((n) => n.title || n.text);
  const tips      = (r.professional_tips || []).filter((n) => n.title || n.text);
  const hasCriticals = criticals.length > 0;
  const hasTips      = tips.length > 0;

  const storageItems = (r.storage_items || []).filter(
    (s) => s.condition || s.duration || s.note);
  const StorageSectionEl = storageItems.length
    ? <StorageSection items={storageItems} mode={mode} />
    : null;

  const NotesSection = (hasCriticals || hasTips) ? (
    <div className={isMobile ? "mt-10" : "mt-14"} data-testid="recipe-notes-section">
      {isMobile ? (
        <div className="space-y-10">
          {hasCriticals && (
            <NoteColumn kind="critical" title="Важно" items={criticals} />
          )}
          {hasTips && (
            <NoteColumn kind="tip" title="Советы автора" items={tips} />
          )}
        </div>
      ) : (
        <div className={`grid gap-10 lg:gap-14 ${hasCriticals && hasTips ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
          {hasCriticals && (
            <NoteColumn kind="critical" title="Важно" items={criticals} />
          )}
          {hasTips && (
            <NoteColumn kind="tip" title="Советы автора" items={tips} />
          )}
        </div>
      )}
    </div>
  ) : null;

  // Optional image: only render if a real url is set. When absent, the
  // whole image slot (and its reserved height) is removed from preview so
  // the ingredients area uses the full available width.
  const hasImage = !!(r.hero && r.hero.url);

  // Desktop: balance groups into 1 or 2 columns depending on count.
  const desktopCols = groups.length >= 3 ? 2 : 1;
  const buckets = distributeGroups(groups, desktopCols);

  return (
    <section className="w-full overflow-hidden"
             style={{ background: "#EFE6D5" }}
             data-testid={`recipe-render-${block.id}-${mode}`}>
      <div className={`mx-auto px-6 sm:px-10 py-10 sm:py-14 ${isMobile ? "" : "max-w-[1200px]"}`}>
        {isMobile ? (
          <>
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 mb-4 text-[10px] tracking-[0.24em] uppercase"
                   style={{ color: "#8A6A46" }}>
                <ChefHat className="w-3.5 h-3.5" /> Рецепт
              </div>
              {Header}
            </div>
            {hasImage && (
              <div className="mb-4">
                <HeroFrame hero={r.hero} aspect="aspect-[4/5]" />
              </div>
            )}
            {YieldCard && <div className="mb-6">{YieldCard}</div>}
            <div className="space-y-8">
              {groups.map((g) => <GroupCard key={g.id} group={g} />)}
            </div>
            {TechnologySection}
            {NotesSection}
            {StorageSectionEl}
          </>
        ) : (
          <>
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 mb-4 text-[10px] tracking-[0.24em] uppercase"
                   style={{ color: "#8A6A46" }}>
                <ChefHat className="w-3.5 h-3.5" /> Рецепт
              </div>
              {Header}
              {YieldCard}
            </div>
            {hasImage ? (
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-10 lg:gap-14 items-start">
                <div>
                  <HeroFrame hero={r.hero} aspect="aspect-[4/5]" />
                </div>
                <div>
                  <div className={`grid gap-x-10 gap-y-10 ${desktopCols === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
                    {buckets.map((col, ci) => (
                      <div key={ci} className="space-y-8">
                        {col.map((g) => <GroupCard key={g.id} group={g} />)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className={`grid gap-x-10 gap-y-10 ${desktopCols === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
                {buckets.map((col, ci) => (
                  <div key={ci} className="space-y-8">
                    {col.map((g) => <GroupCard key={g.id} group={g} />)}
                  </div>
                ))}
              </div>
            )}
            {TechnologySection}
            {NotesSection}
            {StorageSectionEl}
          </>
        )}
      </div>
    </section>
  );
}
